/**
 * Shared event-expiry helpers.
 *
 * The rule: an event is expired once its real end moment is in the past.
 *   • With `ends_at`     → use that moment.
 *   • Without `ends_at`  → use END-OF-DAY on the start date (23:59:59 local).
 *
 * Why end-of-day for start-only events: when a creator sets only a start
 * time (e.g. "4 PM today"), they almost always mean "the event happens
 * sometime today starting at 4", not "the event ends the instant it
 * begins". Flipping to Expired at the start moment was annoying: an
 * event starting at 4 PM looked Expired at 4:01 PM while people were
 * still arriving. End-of-day in the viewer's local TZ matches how
 * people naturally think about "today's event".
 *
 * Timezones: backend stores starts_at/ends_at as UTC strings
 * (helpers/common.getDateTime -> toISOString, see
 * `backend_stores_utc_via_toisostring` memory). We parse as UTC, then
 * `setHours(23,59,59,999)` operates on the Date's local-TZ fields, so
 * "end of start day" is in the viewer's own clock — which is what a
 * viewer reads a bare "Mon, Oct 20" as.
 */

/** Parse a backend datetime string ("YYYY-MM-DD HH:MM:SS" or ISO) as UTC. */
export function parseEventDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const iso = s.includes('T')
    ? (s.endsWith('Z') ? s : s + 'Z')
    : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Compute the effective "end moment" of an event in ms since epoch.
 *  Returns null when neither anchor parses. */
export function eventEndMs(startsAt: string | null | undefined, endsAt?: string | null): number | null {
  const end = parseEventDate(endsAt);
  if (end) return end.getTime();
  const start = parseEventDate(startsAt);
  if (!start) return null;
  // End-of-day on the start date, in the viewer's local TZ.
  const eod = new Date(start);
  eod.setHours(23, 59, 59, 999);
  return eod.getTime();
}

/** True when the event's effective end moment has passed. */
export function isEventExpired(startsAt: string | null | undefined, endsAt?: string | null): boolean {
  const end = eventEndMs(startsAt, endsAt);
  return end != null && end < Date.now();
}
